import * as React from "react";
import { useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

// import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";

//icons
import DownloadIcon from "mdi-material-ui/Download";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import { Button, showNotification } from "jazasoft";

// local

import MuiTable from "../../../components/MuiTable";

import useMutation from "../../../hooks/useMutation";
import { isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { useDeepMemo, useSetting, useTenant, useUnit } from "../../../hooks";
import { getGroupName } from "../../grouping/groupingHelper";
import SettingKeys from "../../../utils/SettingKeys";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { convertTableToCell } from "../../../utils/excelHelper";
import { RollType } from "../../../utils/types";

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3),
    marginTop: "0.5em"
  },
  button: {
    margin: "0 1em"
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "&>*": {
      marginRight: "1em"
    }
  },
  drawerHeader: {
    display: "flex",
    minWidth: 680,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const FabricReleaseDrawer = props => {
  const classes = useStyles();
  const tenant = useTenant();
  const { allUnitList } = useUnit();

  const { open, orderFlow, layList, fabricRelease, onDrawerClose, roles, hasAccess, infoRow } = props;

  const [download, { loading: downloading }] = useMutation({ rawResponse: true });
  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const fabReleaseConfirmation = useSetting(SettingKeys.FABRIC_RELEASE_CONFIRMATION);

  const rollList =
    fabricRelease?.rollInfoList
      ?.map(ri => ({ ...ri, externalIds: !isEmpty(ri.externalIds) ? JSON.parse(ri.externalIds) : [] }))
      .map(ri => ({ ...ri, ackNo: ri.externalIds.find(e => e.appId === "ACK_NO")?.externalId })) || [];

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "rollNo", title: "Factory Roll No" },
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "invStatus", title: "Inv. Status" },
      { dataKey: "weight", title: "Weight", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} kg`) },
      { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 2)} m`) },
      { dataKey: "width", title: "Cutable Width", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 1)} cm`) },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "groupName", title: "Group", length: 50 },
      { dataKey: "layNo", title: "Lay No" },
      { dataKey: "ackNo", title: "Ack No", filterOptions: { filter: true } }
    ];
    if (rollList.every(ri => isEmpty(ri.ackNo))) {
      cols = cols.filter(c => c.dataKey !== "ackNo");
    }
    if (rollList.every(e => isEmpty(e.weight) || e.weight === 0.0)) {
      cols = cols.filter(c => c.dataKey !== "weight");
    }
    return cols;
  }, [rollList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList) || rollList.length === 1) return rollList;

    const layRollList = [];
    const addedRollIds = new Set();
    layList
      .sort((a, b) => a.layNo - b.layNo)
      .forEach(lay => {
        lay.rowList.forEach(row => {
          if (!addedRollIds.has(row.rollInfo.id)) {
            layRollList.push({ ...row.rollInfo, lay });
            addedRollIds.add(row.rollInfo.id);
          }
        });
      });

    const finalRollList = rollList
      .map(roll => {
        const lay = layRollList.find(e => e.id === roll.id)?.lay;
        const group = orderFlow?.groupList?.find(e => e.id === roll.finalGroupId);
        return {
          ...roll,
          group,
          lay,
          groupName: getGroupName(group),
          layNo: lay?.layNo
        };
      })
      .sort((a, b) => (a.layNo !== b.layNo ? a.layNo - b.layNo : a.group?.name < b.group?.name ? -1 : 1));

    const total = rollList.reduce((acc, r) => acc + r.length, 0);
    return [...finalRollList, { rollNo: "Total", length: toFixed(total, 2) }];
  }, [rollList, orderFlow, layList]);

  const onDownload = () => {
    const filename = `FABRIC_RELEASE`.replaceAll(" ", "-").replaceAll("/", "-");
    const unit = allUnitList?.find(e => e.unitId === infoRow?.unitId);

    const orderInfoColumns = [
      { dataKey: "erpNo", title: "ERP No" },
      { dataKey: "fpo", title: "FPO" },
      { dataKey: "unit", title: "Unit" },
      { dataKey: "bpo", title: "BPO" },
      { dataKey: "customer", title: "Customer" },
      { dataKey: "product", title: "Product" },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "inseam", title: "Inseam" },
      { dataKey: "destination", title: "Destination" },
      { dataKey: "deliveryDate", title: "Delivery Date" },
      { dataKey: "part", title: "Part" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty" }
    ];

    const fabricInfoColumns = [
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "fabricColor", title: "Fabric Color" },
      { dataKey: "itemDescription", title: "Item Description" },
      { dataKey: "articleName", title: "Article Name" },
      { dataKey: "allocatedQty", title: "Allocated Qty" },
      { dataKey: "transactionNo", title: "Transaction No" },
      { dataKey: "challanNo", title: "Challan No" },
      { dataKey: "releaseDate", title: "Release Date" },
      { dataKey: "releasedBy", title: "Released By" },
      { dataKey: "releaseQty", title: "Released Qty" }
    ];

    const rollInfoList = fabricRelease?.rollInfoList;

    const orderInfoRows = [
      {
        erpNo: infoRow?.poRef,
        fpo: infoRow?.fpo,
        unit: allUnitList?.find(e => e.unitId === infoRow?.unitId)?.name || "",
        bpo: infoRow?.customerFlowRefs,
        customer: infoRow?.customer,
        product: infoRow?.product?.name,
        style: infoRow?.styles,
        color: infoRow?.color,
        inseam: infoRow?.inseam,
        destination: infoRow?.destination,
        deliveryDate: infoRow?.deliveryDate,
        part: infoRow?.part,
        lot: infoRow?.lot,
        orderQty: infoRow?.orderQty
      }
    ];

    const fabricInfoRows = [
      {
        itemCode: infoRow?.itemCode,
        fabricColor: infoRow?.fabColor,
        itemDescription: infoRow?.itemDesc,
        articleName: infoRow?.articleName,
        allocatedQty: infoRow?.allocatedQty,
        transactionNo: fabricRelease?.txn,
        challanNo: fabricRelease?.challanNo,
        releaseDate: fabricRelease?.releasedDate,
        releasedBy: fabricRelease?.releasedBy,
        releaseQty: fabricRelease?.releasedQty,
        extraFabric: rollInfoList?.filter(e => e.rollType === "EXCESS").reduce((acc, rol) => acc + rol?.length, 0) || 0
      }
    ];

    const extraRows = rows.filter(row => row.rollType === RollType.EXTRA && row.rollNo !== "Total");
    const normalRows = rows.filter(row => row.rollType !== RollType.EXTRA && row.rollNo !== "Total");

    const extraTotalRow = {
      rollNo: "Total",
      length: extraRows.reduce((acc, e) => acc + e.length, 0),
      weight: extraRows.reduce((acc, e) => acc + (e.weight || 0), 0)
    };
    const normalTotalRow = {
      rollNo: "Total",
      length: normalRows.reduce((acc, e) => acc + e.length, 0),
      weight: normalRows.reduce((acc, e) => acc + (e.weight || 0), 0)
    };

    const maxColNo = orderInfoColumns.length;
    const baiscInfoCells = convertTableToCell(orderInfoColumns, orderInfoRows, 4, maxColNo);
    const fabricInfoCells = convertTableToCell(fabricInfoColumns, fabricInfoRows, 6 + orderInfoRows.length, maxColNo);
    const plannedRollCells = convertTableToCell(columns, [...normalRows, normalTotalRow], 8 + orderInfoRows.length + fabricInfoRows.length, maxColNo);
    const extraRollCells = isEmpty(extraRows)
      ? []
      : convertTableToCell(columns, [...extraRows, extraTotalRow], 11 + orderInfoRows.length + fabricInfoRows.length + normalRows.length, maxColNo);

    let cellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: unit?.displayName || unit?.name || tenant?.name || "",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Fabric Release Report",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      {
        rowNo: 3,
        colNo: 1,
        value: "Order Info",
        colEnd: maxColNo,
        cellStyle: { fontHeight: 15, bold: true }
      },
      ...baiscInfoCells,
      {
        rowNo: 5 + orderInfoRows.length,
        colNo: 1,
        value: "Fabric Info",
        colEnd: maxColNo,
        cellStyle: { fontHeight: 15, bold: true }
      },
      ...fabricInfoCells,
      {
        rowNo: 7 + orderInfoRows.length + fabricInfoRows.length,
        colNo: 1,
        value: "Planned Rolls",
        colEnd: maxColNo,
        cellStyle: { fontHeight: 15, bold: true }
      },
      ...plannedRollCells
    ];

    if (!isEmpty(extraRollCells)) {
      cellList = [
        ...cellList,
        {
          rowNo: 10 + orderInfoRows.length + fabricInfoRows.length + normalRows.length,
          colNo: 1,
          value: "Extra Rolls",
          colEnd: maxColNo,
          cellStyle: { fontHeight: 15, bold: true }
        },
        ...extraRollCells
      ];
    }

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      params: { filename },
      data: [{ sheetNo: 0, sheetName: "Fabric Release Report", autoSizeColumn: true, excelData: { cellList } }]
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
        }
      }
    });
  };

  const onFooterActionClick = (e, action, rows, _, onActionComplete) => {
    const options = {
      url: `fabric-release/ack`,
      method: "PUT",
      params: { id: fabricRelease?.id },
      data: fabricRelease
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Fabric release confirmed successfully"));
        onActionComplete(rows);
        onDrawerClose();
      }
    });
  };

  const status = fabricRelease?.rollInfoList?.some(r => r.invStatus === "Issued") ? "Issued" : "Requested";

  const footerActions =
    hasPrivilege(roles, hasAccess, ResourceId.FABRIC_RELEASE, Scope.UPDATE) &&
    "Integrated (Confirmed Manually in OptaCut)" === fabReleaseConfirmation &&
    status !== "Issued"
      ? [{ id: "confirm-release", name: "Confirm Fabric Release", options: { variant: "contained", color: "primary" } }]
      : [];

  return (
    <div>
      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="h5">Fabric Release Rolls</Typography>
            <Button label="Download" showLabel variant="outlined" color="primary" disabled={downloading} onClick={onDownload}>
              <DownloadIcon />
            </Button>
          </div>
        </div>
        <Divider />

        <MuiTable
          title="Released Rolls"
          columns={columns}
          rows={rows}
          searchable
          searchKeys={["rollNo"]}
          footerActions={footerActions}
          onFooterActionClick={onFooterActionClick}
          cellStyle={({ row, rowIdx }) => ({ fontWeight: row.rollNo === "Total" ? "bold" : "normal" })}
          i18nKey="resources.fab-release.releasedRolls"
        />
      </Drawer>
    </div>
  );
};

export default FabricReleaseDrawer;
